import React  from 'react'
import { useResizeObserver } from '@wojtekmaj/react-hooks'
import { pdfjs, Document, Page, Thumbnail } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import useScreenSize from '../../../hocs/useScreenSize'
import ArrowHead from '../../../assets/icons/arrow-head-right.svg?react'
import Button from '../form/Button'
import ZoomMinusIcon from '../../../assets/icons/zoom-minus.svg?react'
import ZoomPlusIcon from '../../../assets/icons/zoom-plus.svg?react'
import RotateIcon from '../../../assets/icons/rotate.svg?react'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString()

const options = {
  cMapUrl: '/cmaps/',
  standardFontDataUrl: '/standard_fonts/',
}

const resizeObserverOptions = {}
const SIDE_BAR_WIDTH = 120

type Props = {
  pdfFile: string | File
  isFullScreen?: boolean
  hideThumbnails?: boolean
}
const PdfViewer: React.FC<Props> = ({ pdfFile, isFullScreen, hideThumbnails }) => {
  const screenSize = useScreenSize()
  const [numPages, setNumPages] = React.useState<number>()
  const [containerWidth, setContainerWidth] = React.useState<number>(100)
  const [containerRef, setContainerRef] = React.useState<HTMLElement | React.RefObject<HTMLElement> | null>(null)
  const pageRefs = React.useMemo(() => {
		return Array.from(new Array(numPages), () => React.createRef())
	}, [numPages])
  const [currentScale, setCurrentScale] = React.useState<number>(1)
  const getDefaultScale = () => {
    if (screenSize.isXs) return 1
    if (screenSize.isSm) return 1
    if (screenSize.isMd) return 1
    return 1.2
  }
  const [currentPage, setCurrentPage] = React.useState<number>(getDefaultScale())
  const [rotate, setRotate] = React.useState<number>(0)

  const onResize = React.useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries
    if (entry) {
      setContainerWidth(entry.contentRect.width)
    }
  }, [])

  useResizeObserver(containerRef as Element, resizeObserverOptions, onResize)

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const PageThumbnails = React.useMemo(() => {
    const onClick = ({ pageNumber }: { pageNumber: number }) => {
      setCurrentPage(pageNumber)
      scrollToRef(pageRefs[pageNumber - 1] as React.RefObject<HTMLElement>)
    }
    return (
      <div className='min-w-32'>
        {Array.from(new Array(numPages), (_, index) => (
          <div className='mb-6 mr-4' key={`thumbnail_${index + 1}`}>
            <Thumbnail
              pageNumber={index + 1}
              width={SIDE_BAR_WIDTH}
              onItemClick={onClick}
            />
            <div className='text-center mt-2 text-xs text-deep-green'><p>{index + 1}</p></div>
          </div>
        ))}
      </div>
    )
  }, [numPages])

  const PageContent = React.useMemo(() => {
    const getPageWidth = () => {
      const maxWidth = 800
      const capWidth = (width: number) => width > maxWidth ? maxWidth : width

      return capWidth((() => {
        if (screenSize.isXs) return containerWidth
        if (screenSize.isSm) return containerWidth - 200
        if (screenSize.isMd) return containerWidth - 300
        return containerWidth / 2
      })())
    }

    if(isFullScreen || screenSize.isMd) {
      // In Desktop and when Fullscreen reader we show all pages
      return (
        <>
          {Array.from(new Array(numPages), (_, index) => (
            <div ref={pageRefs[index] as React.RefObject<HTMLDivElement>} key={`page_${index + 1}`}>
              <Page pageNumber={index + 1} width={getPageWidth()} scale={currentScale} />
              <div className='h-2 w-full' />
            </div>
          ))}
        </>
      )
    }

    // In Mobile we show only 1 page at a time
    return <Page pageNumber={currentPage} width={getPageWidth()} scale={currentScale} />
  }, [containerWidth, currentPage, currentScale, rotate, numPages])

  const scrollToRef = (ref: React.RefObject<HTMLElement>) => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const PageNavigationButton = () => {
    type ChangePageButtonProps = { children: React.ReactNode, value: number }
    const ChangePageButton: React.FC<ChangePageButtonProps> = ({ children, value }) => {
      const onClick = () => {
        setCurrentPage(currentPage + value)
        scrollToRef(containerRef as React.RefObject<HTMLElement>)
      }
      return (
        <button className='px-2' onClick={onClick}>{children}</button>
      )
    }

    return (
      <div className='text-white text-xs rounded-full bg-deep-green p-2 w-30 z-20 flex items-center'>
        <ChangePageButton value={-1}><ArrowHead className='rotate-180 stroke-white' width={18} height={18} /></ChangePageButton>
        {currentPage}/{numPages}
        <ChangePageButton value={1}><ArrowHead className='stroke-white' width={18} height={18} /></ChangePageButton>
      </div>
    )
  }

  const PdfControls = () => {
    const rotateClockWise = () => {
      setRotate(rotate + 90)
    }

    return (
      <div className='sticky top-5 pe-6 w-full z-20 py-2 text-md'>
        <div className='hidden sm:flex w-full sticky space-x-2 justify-end items-end'>
          <Button size='sm' variant='primary' onClick={() => { setCurrentScale(currentScale + 0.1) }} icon={<ZoomPlusIcon />} />
          <Button size='sm' variant='primary' onClick={() => { setCurrentScale(currentScale - 0.1) }} icon={<ZoomMinusIcon />} />
          <Button size='sm' variant='primary' onClick={rotateClockWise} icon={<RotateIcon />} />
        </div>
      </div>
    )
  }

  return (
    <div className='relative'>
      <PdfControls />
      <div className='relative sm:bg-soft-green sm:p-6 rounded-lg shadow-gray-100'>
        <div ref={setContainerRef}>
          <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess} options={options} rotate={rotate}>
            <div className='flex'>
              {!hideThumbnails && (
                <div className='hidden sm:block max-h-[900px] overflow-y-scroll lg:px-16 box-border'>
                  {PageThumbnails}
                </div>
              )}
              <div className='sm:ml-6 md:mx-auto relative max-h-[900px] overflow-y-scroll'>
                {PageContent}
                {!isFullScreen && (
                  <div className='absolute block bottom-2 left-0 w-full'>
                    <div className='md:hidden text-center flex justify-center'>
                      <PageNavigationButton />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Document>
        </div>
      </div>
    </div>
  )
}

export default React.memo(PdfViewer, ({pdfFile: prevPdfFile}, {pdfFile: nextPdfFile}) => prevPdfFile !== nextPdfFile)
